import { useContext } from 'react';

import { useQuery } from '@tanstack/react-query';
import { queryClient } from 'QueryClientProvider';

import { UserContext } from '@contexts/UserContext';

import {
    loadActiveShowSeasonsForContext,
    loadSeasons,
    loadShowSeasonDetails,
} from '@containers/Booking/ShowSeasons/showSeasonsRepository';
import {
    getActiveSeasonModelIds,
    isSeasonArchived,
    isSeasonInActiveContext,
    isSeasonModelCancelled,
    normalizeSeason,
    normalizeSeasonDetails,
    sortSeasonsDesc,
    toActiveShowSeason,
} from '@containers/Booking/ShowSeasons/showSeasonsHelpers';

export const SHOW_SEASONS_LIST_KEY = ['showseasons'];
const ACTIVE_SHOW_SEASONS_KEY = ['activeshowseasons'];
const getShowSeasonQueryKey = (seasonId) => ['showseason', Number(seasonId)];

const ACTIVE_SHOW_SEASONS_OPTIONS = {
    staleTime: 60000,
    cacheTime: Infinity,
    retry: false,
};

const SHOW_SEASON_OPTIONS = {
    staleTime: 60000,
    cacheTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
};

const SHOW_SEASONS_LIST_OPTIONS = {
    staleTime: 60000,
    cacheTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
};

const fetchActiveShowSeasons = async () => loadActiveShowSeasonsForContext();

const updateActiveShowSeasonsCache = (updater) => {
    queryClient.setQueryData(ACTIVE_SHOW_SEASONS_KEY, (prev = []) => updater(prev));
};

export const getActiveShowSeasonModelIdSet = (seasonId, activeShowSeasons) => {
    if (!seasonId || !activeShowSeasons?.length) {
        return null;
    }

    const season = activeShowSeasons.find((item) => Number(item.ID) === Number(seasonId));
    if (!season?.Models?.length) {
        return new Set();
    }

    return new Set(season.Models.map((modelId) => Number(modelId)));
};

export const getActiveShowSeasons = () => queryClient.getQueryData(ACTIVE_SHOW_SEASONS_KEY) || [];

export const getActiveShowSeason = (seasonId) => {
    return getActiveShowSeasons().find((season) => Number(season.ID) === Number(seasonId)) || null;
};

export const upsertActiveShowSeasonInCache = (season, modelIds = []) => {
    const normalizedSeason = normalizeSeason(season);

    if (!normalizedSeason?.ID) {
        return;
    }

    const activeSeason = toActiveShowSeason(
        normalizedSeason,
        modelIds.length ? modelIds : getActiveShowSeason(normalizedSeason.ID)?.Models || []
    );

    updateActiveShowSeasonsCache((prev) => {
        const index = prev.findIndex((item) => Number(item.ID) === Number(normalizedSeason.ID));

        if (index === -1) {
            return [...prev, activeSeason];
        }

        const next = [...prev];
        next[index] = activeSeason;
        return next;
    });
};

export const removeActiveShowSeasonFromCache = (seasonId) => {
    updateActiveShowSeasonsCache((prev) => prev.filter((season) => Number(season.ID) !== Number(seasonId)));
};

export const addModelToActiveShowSeasonCache = (seasonId, modelId) => {
    updateActiveShowSeasonsCache((prev) =>
        prev.map((season) => {
            if (Number(season.ID) !== Number(seasonId)) {
                return season;
            }

            const models = [...(season.Models || [])];
            if (!models.some((id) => Number(id) === Number(modelId))) {
                models.push(Number(modelId));
            }

            return { ...season, Models: models };
        })
    );
};

export const addModelsToActiveShowSeasonCache = (seasonId, modelIds) => {
    if (!modelIds?.length) {
        return;
    }

    updateActiveShowSeasonsCache((prev) =>
        prev.map((season) => {
            if (Number(season.ID) !== Number(seasonId)) {
                return season;
            }

            const modelsSet = new Set((season.Models || []).map((id) => Number(id)));
            modelIds.forEach((modelId) => modelsSet.add(Number(modelId)));

            return { ...season, Models: [...modelsSet] };
        })
    );
};

export const removeModelFromActiveShowSeasonCache = (seasonId, modelId) => {
    updateActiveShowSeasonsCache((prev) =>
        prev.map((season) => {
            if (Number(season.ID) !== Number(seasonId)) {
                return season;
            }

            return {
                ...season,
                Models: (season.Models || []).filter((id) => Number(id) !== Number(modelId)),
            };
        })
    );
};

export const syncSeasonModelInActiveShowSeasonCache = (seasonModel, fallbackSeasonModel = null) => {
    const mergedSeasonModel = {
        ...fallbackSeasonModel,
        ...seasonModel,
    };
    const seasonId = mergedSeasonModel.SeasonID;
    const modelId = mergedSeasonModel.ModelID;

    if (!seasonId || modelId == null) {
        return;
    }

    if (isSeasonModelCancelled(mergedSeasonModel)) {
        removeModelFromActiveShowSeasonCache(seasonId, modelId);
        return;
    }

    addModelToActiveShowSeasonCache(seasonId, modelId);
};

export const syncActiveShowSeasonInCache = (season, currentUser, modelIds = null) => {
    if (!season || !currentUser?.ActiveContext) {
        return;
    }

    const normalizedSeason = normalizeSeason(season);

    if (!normalizedSeason?.ID) {
        return;
    }

    if (isSeasonArchived(normalizedSeason) || !isSeasonInActiveContext(normalizedSeason, currentUser.ActiveContext)) {
        removeActiveShowSeasonFromCache(normalizedSeason.ID);
        return;
    }

    const models =
        modelIds ||
        getActiveShowSeason(normalizedSeason.ID)?.Models ||
        [];

    upsertActiveShowSeasonInCache(normalizedSeason, models);
};

export const upsertSeasonInListCache = (season) => {
    const normalizedSeason = normalizeSeason(season);

    if (!normalizedSeason?.ID) {
        return;
    }

    queryClient.setQueryData(SHOW_SEASONS_LIST_KEY, (prev = []) => {
        const index = prev.findIndex((item) => Number(item.ID) === Number(normalizedSeason.ID));

        if (index === -1) {
            return sortSeasonsDesc([...prev, normalizedSeason]);
        }

        const next = [...prev];
        next[index] = { ...next[index], ...normalizedSeason };
        return sortSeasonsDesc(next);
    });
};

export const removeSeasonFromListCache = (seasonId) => {
    queryClient.setQueryData(SHOW_SEASONS_LIST_KEY, (prev = []) =>
        prev.filter((season) => Number(season.ID) !== Number(seasonId))
    );
};

export const upsertShowSeasonDetailsInCache = (seasonDetails) => {
    const normalizedDetails = normalizeSeasonDetails(seasonDetails);

    if (!normalizedDetails?.ID) {
        return;
    }

    queryClient.setQueryData(getShowSeasonQueryKey(normalizedDetails.ID), (prev) => ({
        ...prev,
        ...normalizedDetails,
        Models: normalizedDetails.Models ?? prev?.Models ?? [],
    }));
};

export const addSeasonModelsToDetailsCache = (seasonId, seasonModels) => {
    if (!seasonId || !seasonModels?.length) {
        return;
    }

    queryClient.setQueryData(getShowSeasonQueryKey(seasonId), (prev) => {
        if (!prev) {
            return prev;
        }

        const existingIds = new Set((prev.Models || []).map((seasonModel) => Number(seasonModel.ID)));
        const nextModels = [...(prev.Models || [])];

        seasonModels.forEach((seasonModel) => {
            const normalizedSeasonModel = {
                ...seasonModel,
                SeasonID: seasonModel.SeasonID ?? Number(seasonId),
            };

            if (!existingIds.has(Number(normalizedSeasonModel.ID))) {
                nextModels.push(normalizedSeasonModel);
            }
        });

        return {
            ...prev,
            Models: nextModels,
        };
    });
};

export const updateSeasonModelInDetailsCache = (seasonId, seasonModel, fallbackSeasonModel = null) => {
    if (!seasonId || !seasonModel?.ID) {
        return;
    }

    queryClient.setQueryData(getShowSeasonQueryKey(seasonId), (prev) => {
        if (!prev) {
            return prev;
        }

        return {
            ...prev,
            Models: (prev.Models || []).map((item) => {
                if (Number(item.ID) !== Number(seasonModel.ID)) {
                    return item;
                }

                return {
                    ...item,
                    ...fallbackSeasonModel,
                    ...seasonModel,
                    SeasonID: seasonModel.SeasonID ?? fallbackSeasonModel?.SeasonID ?? item.SeasonID ?? Number(seasonId),
                    ModelID: seasonModel.ModelID ?? fallbackSeasonModel?.ModelID ?? item.ModelID,
                };
            }),
        };
    });
};

export const removeSeasonModelFromDetailsCache = (seasonId, seasonModelId) => {
    queryClient.setQueryData(getShowSeasonQueryKey(seasonId), (prev) => {
        if (!prev) {
            return prev;
        }

        return {
            ...prev,
            Models: (prev.Models || []).filter((item) => Number(item.ID) !== Number(seasonModelId)),
        };
    });
};

export const useShowSeasonsList = () => {
    return useQuery({
        queryKey: SHOW_SEASONS_LIST_KEY,
        queryFn: loadSeasons,
        ...SHOW_SEASONS_LIST_OPTIONS,
    });
};

export const useActiveShowSeasons = () => {
    const { currentUser } = useContext(UserContext);

    return useQuery({
        queryKey: ACTIVE_SHOW_SEASONS_KEY,
        queryFn: fetchActiveShowSeasons,
        ...ACTIVE_SHOW_SEASONS_OPTIONS,
        enabled: !!currentUser?.ActiveContext,
    });
};

export const prefetchActiveShowSeasons = () => {
    return queryClient.prefetchQuery({
        queryKey: ACTIVE_SHOW_SEASONS_KEY,
        queryFn: fetchActiveShowSeasons,
        ...ACTIVE_SHOW_SEASONS_OPTIONS,
    });
};

export const forceReloadActiveShowSeasons = () => {
    return queryClient.invalidateQueries({ queryKey: ACTIVE_SHOW_SEASONS_KEY });
};

export const forceReloadShowSeasonsList = () => {
    return queryClient.invalidateQueries({ queryKey: SHOW_SEASONS_LIST_KEY });
};

export const fetchShowSeasonDetails = async (seasonId) => {
    if (!seasonId) {
        return null;
    }

    return loadShowSeasonDetails(seasonId);
};

export const getShowSeasonDetails = (seasonId) => {
    if (!seasonId) {
        return null;
    }

    return queryClient.getQueryData(getShowSeasonQueryKey(seasonId)) || null;
};

export const useShowSeason = (seasonId) => {
    return useQuery({
        queryKey: getShowSeasonQueryKey(seasonId),
        queryFn: () => fetchShowSeasonDetails(seasonId),
        ...SHOW_SEASON_OPTIONS,
        enabled: !!seasonId,
    });
};

export const prefetchShowSeason = (seasonId) => {
    if (!seasonId) {
        return Promise.resolve(null);
    }

    return queryClient.prefetchQuery({
        queryKey: getShowSeasonQueryKey(seasonId),
        queryFn: () => fetchShowSeasonDetails(seasonId),
        ...SHOW_SEASON_OPTIONS,
    });
};

export const forceReloadShowSeason = (seasonId) => {
    if (!seasonId) {
        return Promise.resolve();
    }

    return queryClient.invalidateQueries({ queryKey: getShowSeasonQueryKey(seasonId) });
};

export const removeShowSeasonFromCache = (seasonId) => {
    queryClient.removeQueries({ queryKey: getShowSeasonQueryKey(seasonId) });
};

export const syncShowSeasonDetailsFromSeason = (season, seasonModels = null) => {
    const normalizedSeason = normalizeSeason(season);
    const currentDetails = getShowSeasonDetails(normalizedSeason.ID);

    upsertShowSeasonDetailsInCache({
        ...currentDetails,
        ...normalizedSeason,
        Models: seasonModels ?? currentDetails?.Models ?? [],
    });
};

export const syncActiveShowSeasonModelsFromDetails = (seasonDetails) => {
    if (!seasonDetails?.ID) {
        return;
    }

    upsertActiveShowSeasonInCache(seasonDetails, getActiveSeasonModelIds(seasonDetails.Models));
};
